import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ArrowRight, Wine, Star, Grape, Lock, Sparkles, Send, ShoppingBag, Store } from "lucide-react";
import WineCard from "@/components/WineCard";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

import { API } from "@/lib/apiBase";

export default function Landing() {
  const [featured, setFeatured] = useState([]);
  const { t } = useLanguage();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    axios
      .get(`${API}/wines/featured`)
      .then((r) => setFeatured(r.data))
      .catch((err) => console.error("Failed to load featured wines", err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Public top-rated wines — used by both the Ranked-by-Score teaser (logged-out)
  // and the score-badges showcase further down
  const [topRated, setTopRated] = useState([]);
  useEffect(() => {
    axios
      .get(`${API}/wines/top-rated?limit=6`)
      .then((r) => setTopRated(Array.isArray(r.data) ? r.data : []))
      .catch((err) => console.error("Failed to load top-rated wines", err));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main data-testid="landing-page">
      {/* Hero */}
      <section className="hero-section">
        <div className="hero-bg" style={{ backgroundImage: `url(https://images.unsplash.com/photo-1620485435764-ba7b5ea3804f?w=1920&q=80)` }} />
        <div className="hero-overlay" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 pt-32 pb-20">
          <div className="max-w-2xl">
            <p className="font-accent text-xs tracking-[0.4em] text-gold/80 mb-6 animate-fade-in">{t.heroSubtitle}</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white/95 leading-[1.1] mb-6 animate-fade-in stagger-1">
              {t.heroTitle1}{t.heroTitle2 && (<><br />{t.heroTitle2}</>)}
            </h1>
            <p className="font-body text-base sm:text-lg text-white/50 leading-relaxed mb-10 max-w-md animate-fade-in stagger-2">
              {t.heroDesc}
            </p>
            <div className="flex items-center gap-4 animate-fade-in stagger-3">
              <Link to="/catalog">
                <Button data-testid="hero-explore-btn" className="bg-merlot hover:bg-merlot-light text-white font-body text-sm tracking-wide px-8 py-5 rounded-sm shadow-[0_0_20px_rgba(158,42,43,0.3)] transition-all duration-300 hover:shadow-[0_0_30px_rgba(158,42,43,0.5)]">
                  {t.exploreCollection} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-[1400px] mx-auto px-6 py-16">
        <div className="grid grid-cols-3 gap-8">
          {[
            { icon: Wine, label: t.curatedWines, value: "20+" },
            { icon: Star, label: t.ratingScale, value: "0-100" },
            { icon: Grape, label: t.grapeVarieties, value: "12+" },
          ].map(({ icon: Icon, label, value }, i) => (
            <div key={label} className={`text-center animate-fade-in stagger-${i + 1}`}>
              <Icon className="w-5 h-5 text-merlot mx-auto mb-3" />
              <p className="font-display text-2xl text-white/90 mb-1">{value}</p>
              <p className="font-body text-xs tracking-wide text-white/40">{label}</p>
            </div>
          ))}
        </div>
      </section>

      <Separator className="max-w-[1400px] mx-auto bg-white/5" />

      {/* Featured / Sign-in gate */}
      {!user ? (
        <>
          {/* Ranked by Score — public teaser, 6 top-rated wines */}
          {topRated.length > 0 && (
            <section className="max-w-[1400px] mx-auto px-6 py-20" data-testid="ranked-by-score">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-3.5 h-3.5 text-gold/70" />
                <p className="font-accent text-xs tracking-[0.3em] text-gold/70">ALFREDWINE.COM</p>
              </div>
              <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
                <h2 className="font-display text-2xl sm:text-3xl text-white/90">Ranked by Score</h2>
                <Link
                  to="/catalog"
                  data-testid="ranked-view-all"
                  className="font-body text-sm text-gold/80 hover:text-gold transition-colors flex items-center gap-1"
                >
                  Browse Full Collection <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {topRated.slice(0, 6).map((w, i) => (
                  <div key={w.id} className={`animate-fade-in stagger-${(i % 4) + 1}`}>
                    <WineCard wine={w} disableFavorite />
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="max-w-[1400px] mx-auto px-6 py-20" data-testid="signin-gate">
          <div className="glass-card rounded-sm p-12 text-center">
            <Lock className="w-8 h-8 text-gold/70 mx-auto mb-5" />
            <p className="font-accent text-xs tracking-[0.3em] text-gold/60 mb-3">MEMBERS ONLY</p>
            <h2 className="font-display text-2xl sm:text-3xl text-white/90 mb-3">Unlock Full Tasting Notes</h2>
            <p className="font-body text-sm text-white/50 mb-8 max-w-md mx-auto">
              Sign in or create a free account to access sommelier tasting
              characteristics, market value and pricing on {" "}
              <span className="text-gold/80">AlfredWine.com</span>.
            </p>
            <div className="flex items-center justify-center gap-3">
              <Link to="/login">
                <Button data-testid="gate-signin-btn" className="bg-merlot hover:bg-merlot-light text-white font-body text-sm h-11 px-6 rounded-sm">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button
                  data-testid="gate-register-btn"
                  variant="outline"
                  className="border-white/15 text-white/80 hover:text-white hover:bg-white/5 font-body text-sm h-11 px-6 rounded-sm"
                >
                  Create Free Account
                </Button>
              </Link>
            </div>
          </div>
        </section>
        </>
      ) : featured.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 py-20" data-testid="featured-wines">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="font-accent text-xs tracking-[0.3em] text-gold/60 mb-2">{t.featured}</p>
              <h2 className="font-display text-2xl sm:text-3xl text-white/90">{t.premierSelection}</h2>
            </div>
            <Link to="/catalog" className="font-body text-sm text-white/40 hover:text-white transition-colors flex items-center gap-1">
              {t.viewAll} <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {featured.map((wine, i) => (
              <div key={wine.id} className={`animate-fade-in stagger-${i + 1}`}>
                <WineCard wine={wine} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* AlfredWine Score Badges — public showcase */}
      {topRated.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 py-20" data-testid="badges-showcase">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <p className="font-accent text-xs tracking-[0.3em] text-gold/60 mb-2">ALFREDWINE SCORE</p>
              <h2 className="font-display text-2xl sm:text-3xl text-white/90">Highest-Rated Bottles</h2>
              <p className="font-body text-sm text-white/40 mt-2 max-w-xl">
                Six top-scoring wines from our cellar. Click any badge to discover the bottle, or
                browse the full collection.
              </p>
            </div>
            <Link
              to="/catalog?sort=rating&order=desc"
              data-testid="badges-view-all"
              className="font-body text-sm text-gold/80 hover:text-gold transition-colors flex items-center gap-1"
            >
              View Full Collection <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6">
            {topRated.map((w, i) => (
              <Link
                key={w.id}
                to={`/wines/${w.id}`}
                data-testid={`badge-thumb-${i}`}
                className={`group relative animate-fade-in stagger-${(i % 4) + 1}`}
              >
                <div className="bg-black/40 rounded-sm p-2 border border-white/5 hover:border-gold/30 transition-all duration-300 group-hover:shadow-[0_0_20px_rgba(212,175,55,0.15)]">
                  <img
                    src={`${API}/wines/${w.id}/badge`}
                    alt={`${w.name} — AlfredWine score ${w.rating}`}
                    width={220}
                    height={280}
                    loading="lazy"
                    className="w-full h-auto block"
                  />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Submit Wines CTA */}
      <section className="max-w-[1400px] mx-auto px-6 py-20" data-testid="submit-wines-home">
        <div className="glass-card rounded-sm p-10 sm:p-14 overflow-hidden relative">
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-merlot/10 blur-3xl pointer-events-none" />
          <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-center">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Send className="w-3.5 h-3.5 text-gold/70" />
                <p className="font-accent text-xs tracking-[0.3em] text-gold/70">FOR WINERIES &amp; IMPORTERS</p>
              </div>
              <h2 className="font-display text-2xl sm:text-3xl text-white/95 mb-3">
                Submit Your Wine for Review
              </h2>
              <p className="font-body text-sm sm:text-base text-white/55 leading-relaxed max-w-2xl">
                Wineries, cellars, importers ... — send us two bottles of your finest and
                AlfredWine.com will review, rate and feature them across our curated collection.
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap lg:flex-nowrap">
              <Link to="/submit-wines">
                <Button
                  data-testid="home-submit-wines-btn"
                  className="bg-merlot hover:bg-merlot-light text-white font-body text-sm h-11 px-6 rounded-sm shadow-[0_0_15px_rgba(158,42,43,0.3)] transition-all duration-300"
                >
                  Submit a Wine <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Marketplace teaser — temporarily hidden per user request */}
      {/* Marketplace seller CTA — temporarily hidden per user request */}

      {/* Categories — Explore by Style */}
      <section className="max-w-[1400px] mx-auto px-6 py-20">
        <p className="font-accent text-xs tracking-[0.3em] text-gold/60 mb-2">{t.categories}</p>
        <h2 className="font-display text-2xl sm:text-3xl text-white/90 mb-12">{t.exploreByStyle}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-5">
          {[
            { name: "Red Wines", img: "https://images.unsplash.com/photo-1700455350486-fea62d5c30df?w=600&q=80", link: "/catalog?grape=red" },
            { name: "White Wines", img: "https://images.unsplash.com/photo-1727060800274-1256eeea33c1?w=600&q=80", link: "/catalog?grape=white" },
            { name: "Sparkling", img: "https://images.unsplash.com/photo-1769697064243-889f2e25d44a?w=600&q=80", link: "/catalog?grape=sparkling" },
            { name: "Rosé", img: "https://images.unsplash.com/photo-1558001373-7b93ee48ffa0?w=600&q=80", link: "/catalog?grape=rose" },
          ].map((cat) => (
            <Link key={cat.name} to={cat.link} className="category-card h-64 block" data-testid={`category-${cat.name.toLowerCase().replace(' ', '-')}`}>
              <img src={cat.img} alt={cat.name} className="w-full h-full object-cover" />
              <div className="absolute bottom-0 left-0 right-0 p-5 z-10">
                <h3 className="font-display text-xl text-white/90">{cat.name}</h3>
                <p className="font-body text-xs text-white/40 mt-1">{t.exploreSelection}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 mt-12">
        <div className="max-w-[1400px] mx-auto px-6 py-12 flex flex-col sm:flex-row items-center justify-between gap-6">
          <a href="https://alfredwine.com/" data-testid="footer-logo">
            <img src="/logo-full.jpg" alt="AlfredWine.com · Global Wine Ratings" className="h-24 sm:h-28 w-auto object-contain rounded-md" />
          </a>
          <p className="font-body text-xs text-white/20">{t.footerText}</p>
        </div>
      </footer>
    </main>
  );
}
